/**
 * onConstructDispatch — re-entrancy guard for the `onConstruct` event node's
 * firing wiring (ENG-981 rework, see the review that rejected the original
 * declarative `headTrackedCamera` node: every node in this event graph must
 * participate via wires, so the missing primitive is an Unreal-style
 * On Construct event — fires at graph (re)construction time, i.e. after
 * hydration and after every applied editor edit).
 *
 * Extracted out of EventRuntime.js purely so it is unit-testable under plain
 * `node --test`: EventRuntime.js transitively imports the asset/API layer
 * (extensionless specifiers only Vite resolves) and cannot be imported under
 * this repo's bare node test runner — same reasoning as eventStarters.js and
 * graphGroup.js's header comments.
 *
 * `wrapDispatch(fire)` returns a function safe to hand straight to
 * `subscribeEventGraph`: if invoking `fire` itself triggers a NESTED graph
 * notification (directly, or via some future side effect of an `onConstruct`
 * chain that edits the graph), the guard makes the nested call a no-op
 * instead of recursing — `onConstruct` fires once per outer notification,
 * never a growing stack of them. A plain module-level boolean is enough here:
 * `notifyEventGraphChange` calls every subscriber synchronously and there is
 * exactly one guarded dispatcher per wrapped `fire`, so there is no
 * concurrent/async re-entrancy to worry about — only a synchronous one.
 */
export function wrapDispatch(fire) {
  let inFlight = false
  return function dispatch(...args) {
    if (inFlight) return
    inFlight = true
    try {
      fire(...args)
    } finally {
      inFlight = false
    }
  }
}

/**
 * createConstructGate — the avatar-loaded gate for the merged
 * beginPlay ∪ onConstruct node (Wave 2B "one node / always playing").
 *
 * beginPlay used to fire once, only after `blueprintReady && loaded`
 * (App.jsx), because voxyConfig / convorcherSession / camera-rig setup need
 * the runtime hooks an unloaded avatar doesn't have yet. onConstruct used to
 * fire on EVERY eventGraphState notification, including boot hydration —
 * before the avatar existed. The merged node must keep both guarantees:
 *
 *   - notify()          — call on every graph notification. Suppressed until
 *                         the gate opens; fires the wrapped dispatch after.
 *   - setReady(true)    — call once the graphs are hydrated AND the avatar
 *                         has loaded. Fires exactly once on the rising edge
 *                         (the old beginPlay fire), regardless of whether a
 *                         notification arrived while closed.
 *   - setReady(false)   — call when the avatar unloads (character switch):
 *                         closes the gate again so the NEXT graph's chains
 *                         wait for the NEXT avatar, exactly like beginPlay did.
 *
 * `state` is an externally-owned `{ ready }` object so EventRuntime.js can
 * stash it on `window` and survive its own HMR re-execution (the same
 * pattern as __ON_CONSTRUCT_UNSUB__ below it) — recreating the gate around
 * the same state never fires and never loses openness. Re-entrancy is
 * handled by wrapDispatch: a construct chain that itself notifies the graph
 * does not recurse.
 */
export function createConstructGate(fire, state = { ready: false }) {
  const dispatch = wrapDispatch(fire)
  return {
    notify() {
      if (state.ready) dispatch()
    },
    setReady(ready) {
      const rising = !!ready && !state.ready
      state.ready = !!ready
      if (rising) dispatch()
    },
    get ready() { return !!state.ready },
  }
}
